import { useEffect, useState } from "react";
import api from "@/lib/api";
import ConfidenceBadge from "@/components/ConfidenceBadge";
import { Section, SectionHead, SourceLink } from "@/components/common";
import { AlertCircle } from "lucide-react";

export default function International() {
  const [countries, setCountries] = useState([]);
  const [selected, setSelected] = useState([]);

  useEffect(() => { api.get("/countries").then((r) => { setCountries(r.data); setSelected(r.data.slice(0, 2).map((c) => c.code)); }); }, []);

  const toggle = (code) => setSelected((p) => p.includes(code) ? p.filter((x) => x !== code) : [...p, code].slice(-4));
  const chosen = countries.filter((c) => selected.includes(c.code));
  const rows = [
    ["facility_count", "Facilities"], ["power_capacity_mw", "Power / electricity"],
    ["water_data", "Water data"], ["cooling_methods", "Cooling methods"], ["policy", "Policy"],
  ];

  return (
    <Section className="py-12 sm:py-16">
      <SectionHead eyebrow="International Explorer" title="Compare countries"
        desc="Select up to four countries to compare. We show the year, units, source, and whether each number is reported, estimated, or modeled. Not all measures are directly comparable — we flag those." />

      <div className="flex flex-wrap gap-2 mb-6">
        {countries.map((c) => (
          <button key={c.code} data-testid={`country-toggle-${c.code}`} onClick={() => toggle(c.code)}
            className={`text-sm px-4 py-2 rounded-lg border transition-colors ${selected.includes(c.code)
              ? "bg-[#2563EB] border-[#2563EB] text-white" : "border-white/10 text-slate-300 hover:border-blue-500/50"}`}>
            {c.name}
          </button>
        ))}
      </div>

      <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 px-5 py-4 mb-6 flex items-start gap-3">
        <AlertCircle size={18} className="text-amber-400 mt-0.5 shrink-0" />
        <p className="text-amber-200/90 text-sm">Cross-country figures often use different definitions and years. Treat these as directional context, not an exact ranking.</p>
      </div>

      {/* Desktop: comparison table */}
      <div className="hidden md:block overflow-x-auto card p-1">
        <table className="w-full text-sm" data-testid="comparison-table">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left p-4 font-mono text-[11px] uppercase tracking-widest text-slate-400">Measure</th>
              {chosen.map((c) => (
                <th key={c.code} className="text-left p-4">
                  <span className="font-display font-bold text-white block">{c.name}</span>
                  <span className="font-mono text-[10px] text-slate-500">{c.year} · {c.unit}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(([key, label]) => (
              <tr key={key} className="border-b border-white/5">
                <td className="p-4 text-slate-400 font-medium align-top">{label}</td>
                {chosen.map((c) => <td key={c.code} className="p-4 text-slate-200 align-top">{c[key] || <span className="text-slate-500 italic">Not publicly reported</span>}</td>)}
              </tr>
            ))}
            <tr>
              <td className="p-4 text-slate-400 font-medium align-top">Confidence &amp; source</td>
              {chosen.map((c) => (
                <td key={c.code} className="p-4 align-top">
                  <ConfidenceBadge level={c.confidence} />
                  {c.source && <div className="mt-2"><SourceLink source={c.source} /></div>}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Mobile: stacked comparison cards (no horizontal scroll) */}
      <div className="md:hidden space-y-4" data-testid="comparison-cards">
        {chosen.map((c) => (
          <div key={c.code} className="card p-5">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <span className="font-display font-bold text-white block">{c.name}</span>
                <span className="font-mono text-[10px] text-slate-500">{c.year} · {c.unit}</span>
              </div>
              <ConfidenceBadge level={c.confidence} />
            </div>
            <dl className="space-y-2">
              {rows.map(([key, label]) => (
                <div key={key} className="flex justify-between gap-4 border-b border-white/5 pb-2">
                  <dt className="text-slate-400 text-xs font-medium shrink-0">{label}</dt>
                  <dd className="text-slate-200 text-sm text-right">{c[key] || <span className="text-slate-500 italic">Not reported</span>}</dd>
                </div>
              ))}
            </dl>
            {c.source && <div className="mt-3"><SourceLink source={c.source} /></div>}
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-4 mt-8">
        {chosen.map((c) => (
          <div key={c.code} className="card p-6">
            <h3 className="font-display font-bold text-xl text-white mb-2">{c.name}</h3>
            <p className="text-slate-400 text-sm">{c.summary}</p>
          </div>
        ))}
      </div>
    </Section>
  );
}
